import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, MenuItem, Modal, Select, Stack, Tab, TextField, Tooltip, Typography, LinearProgress, InputAdornment } from '@mui/material'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { useNavigate, useLocation, useParams, Link } from "react-router-dom"
import { toNumber, round, has, isNull, isNil, isEmpty, isEqual } from 'lodash'
import { useDebounce } from 'use-debounce';

import { useStateContext } from '../../../../context/ContextProvider'
import axios from '../../../../lib/axios'
import { createRegexFromString, deleteZero, handleAxiosError, handleInteger, handleZero, isValid, numericSortComparator } from '../../../../helpers/helper'
import Materials from '../../../../components/inventory/Items'
import AddButton from '../../../../components/AddButton'
import Loading from '../../../../components/Loading'
import CommentHistoryButton from '../../../../components/CommentHistoryButton'
import CommentHistoryModal from '../../../../components/CommentHistoryModal'
import { useAuth } from '../../../../hooks/auth';

/**
 * Report Production has no dedicated endpoint of its own. Entries are added/edited/removed
 * locally in this page (in the `productionReports` state) and only reach the backend when the
 * user hits the page's existing Save or Complete button.
 *
 * GET /api/production-orders/{id} now also returns a "production_reports" array on `data`:
 * {
 *   "production_reports": [
 *     {
 *       "id": 101,
 *       "operation_id": 12,
 *       "operation_label": "10 - Cutting (Saw #1)",
 *       "production_date": "2026-10-12",
 *       "good_qty": 7000,
 *       "scrap_qty": 100,
 *       "scrap_reason": "Damaged Material",
 *       "operators": [
 *         { "employee_id": 5, "employee_name": "John Smith", "labor_hours": 8.0 },
 *         { "employee_id": 9, "employee_name": "Mike Brown", "labor_hours": 8.0 }
 *       ],
 *       "machine_hours": 8.0,
 *       "downtime_hours": 0.5,
 *       "downtime_reason": "Material Shortage"
 *     }
 *   ]
 * }
 *
 * PUT /api/production-orders/{id}/save and PUT /api/production-orders/{id}/complete now also
 * accept a "production_reports" array in their request payload (same entries, "id" present only
 * for rows that already existed when the order was loaded - omitted for rows added in this
 * session so the backend knows to insert them):
 * {
 *   "qty_produced": 7000,
 *   "comments": "...",
 *   "production_reports": [
 *     {
 *       "id": 101,
 *       "operation_id": 12,
 *       "production_date": "2026-10-12",
 *       "good_qty": 7000,
 *       "scrap_qty": 100,
 *       "scrap_reason": "Damaged Material",
 *       "operators": [
 *         { "employee_id": 5, "labor_hours": 8.0 },
 *         { "employee_id": 9, "labor_hours": 8.0 }
 *       ],
 *       "machine_hours": 8.0,
 *       "downtime_hours": 0.5,
 *       "downtime_reason": "Material Shortage"
 *     }
 *   ]
 * }
 *
 * Actual Material Consumption (recorded against a planned material line in the Material List)
 * works the same way - no dedicated endpoint, staged locally in `materials[].actual_consumption`
 * and only sent on Save/Complete.
 *
 * GET /api/production-orders/{id} - each entry in "production_order_items" may now include an
 * "actual_consumption" array:
 * {
 *   "production_order_items": [
 *     {
 *       "id": 1,
 *       ...,
 *       "actual_consumption": [
 *         { "id": 501, "item_id": 20, "item_no": "QO-6051", "description": "2 X 4 X 96 Boards", "quantity": 1.5, "unit_of_measure_code": "BF", "notes": "" },
 *         { "id": 502, "item_id": 22, "item_no": "QO-6053", "description": "2 X 4 X 120 Boards", "quantity": 0.5, "unit_of_measure_code": "BF", "notes": "Used 120\"" }
 *       ]
 *     }
 *   ]
 * }
 *
 * PUT /api/production-orders/{id}/save and PUT /api/production-orders/{id}/complete now also
 * accept a "material_consumption" array (one entry per planned material line that has at least
 * one actual consumption row; "id" on each actual item present only if it already existed):
 * {
 *   "material_consumption": [
 *     {
 *       "production_order_item_id": 1,
 *       "actual_consumption": [
 *         { "id": 501, "item_id": 20, "quantity": 1.5, "unit_of_measure_code": "BF", "notes": "" },
 *         { "item_id": 22, "quantity": 0.5, "unit_of_measure_code": "BF", "notes": "Used 120\"" }
 *       ]
 *     }
 *   ]
 * }
 */

/* TEMP: fake data so the Production Actuals / Recent Entries table can be previewed before "production_reports" is returned by the order GET - remove once the backend is ready */
const MOCK_PRODUCTION_REPORTS = [
    {
        id: 1,
        operation_id: 1,
        operation_label: '10 - Cutting (Saw #1)',
        production_date: '2026-10-12',
        good_qty: 7000,
        scrap_qty: 100,
        scrap_reason: 'Damaged Material',
        operators: [
            { employee_id: 1, employee_name: 'John Smith', labor_hours: 8.0 },
            { employee_id: 2, employee_name: 'Mike Brown', labor_hours: 8.0 }
        ],
        machine_hours: 8.0,
        downtime_hours: 0,
        downtime_reason: ''
    },
    {
        id: 2,
        operation_id: 2,
        operation_label: '20 - Assembly (Assembly Line #1)',
        production_date: '2026-10-13',
        good_qty: 6500,
        scrap_qty: 80,
        scrap_reason: '',
        operators: [
            { employee_id: 1, employee_name: 'John Smith', labor_hours: 8.0 },
            { employee_id: 3, employee_name: 'Sara Lee', labor_hours: 7.5 }
        ],
        machine_hours: 8.0,
        downtime_hours: 0.5,
        downtime_reason: 'Material Shortage'
    }
]

const TEXTFIELD_SX = {
    width: '100%',
    background: '#FCFCFC',
    '.MuiOutlinedInput-input': { padding: '9px !important' },
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
}

const reportModalStyle = {
    minWidth: "360px",
    maxWidth: "900px",
    width: "70%",
    maxHeight: '85vh',
    overflowY: 'auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#ffffff',
    border: 'transparent',
    borderRadius: '5px',
    boxShadow: 24,
    outline: 'none'
}

const ReportStat = ({ label, value }) => (
    <div className='w-full bg-[#FAFAFA] rounded-md p-3'>
        <p className='text-[12px] font-[500] text-[#718096]'>{label}</p>
        <p className='text-[18px] font-[700] text-[#333333] pt-0.5'>{value}</p>
    </div>
)

const DetailField = ({ label, value }) => (
    <div className='flex justify-between items-center' style={{ padding: '15px 10px' }}>
        <span style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#88909C' }}>{label}</span>
        <div style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#171717', textAlign: 'right', maxWidth: '220px' }}>{value}</div>
    </div>
)

const ProcessProductionOrder = () => {
    const { user } = useAuth({ middleware: 'guest' });
    const { t } = useTranslation()
    const { config, currencySymbol, pusher, company_id, setCompany_id } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [random, setRandom] = useState(Math.random())
    const [tabValue, setTabValue] = useState('material')
    const [materials, setMaterials] = useState([])
    const [commentHistory, setCommentHistory] = useState([])
    const [totalCost, setTotalCost] = useState(0)
    const [totalQty, setTotalQty] = useState(0)
    const [statusId, setStatusId] = useState('')
    const [statusName, setStatusName] = useState('')

    const [openCommentHistory, setOpenCommentHistory] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    /* form */
    const [productionOrderNo, setProductionOrderNo] = useState('')
    const [productionOrderDescription, setProductionOrderDescription] = useState('')
    const [itemId, setItemId] = useState('')
    const [itemNo, setItemNo] = useState('')
    const [itemDescription, setItemDescription] = useState('')
    const [itemUnitOfMeasureCode, setItemUnitOfMeasureCode] = useState('')
    const [itemGradeCodeCode, setItemGradeCodeCode] = useState('')
    const [startDate, setStartDate] = useState(dayjs().endOf('day'))
    const [startTime, setStartTime] = useState(dayjs().endOf('minute'))
    const [dueDate, setDueDate] = useState(dayjs().endOf('day'))
    const [comments, setComments] = useState('')
    const [qtyToProduce, setQtyToProduce] = useState(0)
    const [qtyProduced, setQtyProduced] = useState(0)

    const [progressPct, setProgressPct] = useState(0)
    const [progressQty, setProgressQty] = useState(0)
    const [isEditingProgress, setIsEditingProgress] = useState(false)

    const [operations, setoperations] = useState([])
    const [operationOptions, setOperationOptions] = useState([])
    const [rateUomOptions, setRateUomOptions] = useState([])
    const [workCenters, setWorkCenters] = useState([])
    const [siteId, setSiteId] = useState('')

    /* Report Production */
    const [employees, setEmployees] = useState([])
    const [productionReports, setProductionReports] = useState([])
    const [reportModalOpen, setReportModalOpen] = useState(false)
    const [editingReportId, setEditingReportId] = useState(null)
    const [reportOperationId, setReportOperationId] = useState('')
    const [reportDate, setReportDate] = useState(dayjs())
    const [reportGoodQty, setReportGoodQty] = useState('')
    const [reportScrapQty, setReportScrapQty] = useState('')
    const [reportScrapReason, setReportScrapReason] = useState('')
    const [reportOperators, setReportOperators] = useState([])
    const [reportMachineHours, setReportMachineHours] = useState('')
    const [reportDowntimeHours, setReportDowntimeHours] = useState('')
    const [reportDowntimeReason, setReportDowntimeReason] = useState('')

    /* Material Consumption */
    const [allItems, setAllItems] = useState([])
    const [materialConsumptionModalOpen, setMaterialConsumptionModalOpen] = useState(false)
    const [selectedMaterialRow, setSelectedMaterialRow] = useState(null)
    const [consumptionRows, setConsumptionRows] = useState([])

    /* PUSHER */
    useEffect(() => {
        const channelProductionOrderUpdate = pusher.subscribe(`${localStorage.getItem('client_id')}-productionorder-updated-site-${localStorage.getItem('site')}`)
        const channelProductionOrderDelete = pusher.subscribe(`${localStorage.getItem('client_id')}-productionorder-deleted-site-${localStorage.getItem('site')}`)

        channelProductionOrderUpdate.bind(`${localStorage.getItem('client_id')}-productionorder-updated-event-site-${localStorage.getItem('site')}`, data => {
            if (Number(data.id) == Number(params.id)) {
                if (Number(data.production_supervisor) != Number(user?.id)) {
                    Swal.fire({
                        icon: "info",
                        customClass: 'info',
                        showCloseButton: true,
                        iconColor: '#a8d5f1',
                        text: t('production_order_reassigned_text')
                    }).then((result) => {
                        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/production-order', {
                            replace: true
                        })
                    })
                } else {
                    getProductionOrder({
                        id: data.id,
                        dueToPusherEvent: true
                    })
                }
            }
        })
        channelProductionOrderDelete.bind(`${localStorage.getItem('client_id')}-productionorder-deleted-event-site-${localStorage.getItem('site')}`, data => {
            if (Number(data.id) == Number(params.id)) {
                navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/production-order', {
                    replace: true
                })
            }
        })

        return (() => {
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-productionorder-updated-${localStorage.getItem('site')}`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-productionorder-deleted-${localStorage.getItem('site')}`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-item-deleted`)
        })
    }, [])

    useEffect(() => {
        if (isValid(itemId)) {
            const channelItemUpdate = pusher.subscribe(`${localStorage.getItem('client_id')}-item-updated`)

            channelItemUpdate.bind(`${localStorage.getItem('client_id')}-item-updated-event`, data => {
                if (Number(data.id) != Number(itemId)) {
                    return
                }

                getProductionOrder({
                    dueToPusherEvent: true
                })
            })
        }

        return (() => {
            if (isValid(itemId)) {
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-item-deleted`)
            }
        })
    }, [itemId])

    /* KEY EVENT */
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : -1)
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, []);

    /* UseEffect */

    useEffect(() => {
        getProductionOrder({
            id: params.id
        })
    }, [params.id])

    useEffect(() => {
        calculateTotalCost()
        calculateTotalQuantity()
    }, [materials])

    useEffect(() => {
        getOperationNames()
        getProductionRateUom()
    }, [])

    useEffect(() => {
        if (isValid(siteId)) {
            getWorkCenters(siteId)
            getEmployees(siteId)
        }
    }, [siteId])

    useEffect(() => {
        if (!isValid(company_id)) {
            setCompany_id(localStorage.getItem('company_id'))
        }
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getAllItems(company_id)
        }
    }, [company_id])

    /* Handlers */

    const calculateTotalCost = () => {
        let tcost = 0
        materials.forEach(material => {
            tcost += (toNumber(material.qty_per_assembly) * toNumber(material.unit_price)) * toNumber(qtyToProduce)
        })
        setTotalCost(round(tcost, 2))
    }

    const calculateTotalQuantity = () => {
        let tqty = 0
        materials.forEach(material => {
            tqty += toNumber(material.qty_per_assembly) * toNumber(qtyToProduce)
        })
        setTotalQty(tqty)
    }

    const getOperationNames = async () => {
        await axios.get('/api/routing-operation-names', config)
            .then(res => setOperationOptions(res.data?.data ?? []))
            .catch(({ response }) => handleAxiosError({ response: response }))
    }

    const getProductionRateUom = async () => {
        await axios.get('/api/production-rate-uom', config)
            .then(res => setRateUomOptions(res.data?.data ?? []))
            .catch(({ response }) => handleAxiosError({ response: response }))
    }

    const getWorkCenters = async (site) => {
        await axios.get(`/api/work-centers?site_id=${site}`, config)
            .then(res => setWorkCenters(res.data?.data ?? []))
            .catch(({ response }) => handleAxiosError({ response: response }))
    }

    const getEmployees = async (site) => {
        await axios.get(`/api/production-employees?site=${site}`, config)
            .then(res => setEmployees(res.data ?? []))
            .catch(({ response }) => handleAxiosError({ response: response }))
    }

    const getAllItems = async (id) => {
        await axios.get(`/api/items?company_id=${id}`, config)
            .then(res => setAllItems(res.data ?? []))
            .catch(({ response }) => handleAxiosError({ response: response }))
    }

    const parseSetupTime = (value) => {
        if (!isValid(value)) return ''
        if (typeof value === 'string' && value.includes(':')) {
            const [h, m, s] = value.split(':').map(Number)
            return Number((h + (m / 60) + ((s || 0) / 3600)).toFixed(2))
        }
        return Number(value)
    }


    /* API Requests */
    const getProductionOrder = async ({ id = null, dueToPusherEvent = false } = {}) => {
        setIsLoading(true)
        setIsEditingProgress(false)

        await axios
            .get(`/api/production-orders/${isNull(id) ? params.id : id}?with=site,productionSupervisor,productionOrderItems.item,productionOrderItems.materialConsumptions,status,productionOrderOperations,productionActuals`, config)
            .then(res => {
                const data = res.data

                setSiteId(data?.site?.id)
                setProductionOrderNo(data.no)
                setProductionOrderDescription(data.description)
                setStartDate(dayjs(data?.start_date))
                setStartTime(dayjs(data?.start_time))
                setDueDate(dayjs(data?.due_date))
                setStatusId(data?.status?.id)
                setStatusName(data?.status?.name)

                setCommentHistory(data.comment_history)

                setItemId(data?.item?.id)
                setItemNo(data?.item?.item_no)
                setItemDescription(data?.item?.description)
                setItemUnitOfMeasureCode(data?.item?.unit_of_measure?.code)
                setItemGradeCodeCode(data?.item?.grade_code?.code)
                setQtyToProduce(data?.qty_to_produce)
                setQtyProduced(data?.qty_produced)
                setProgressQty(data?.qty_produced)
                setProgressPct(round((Number(data?.qty_produced) / Number(data?.qty_to_produce)) * 100, 2))
                setMaterials(data?.production_order_items.map((item) => ({
                    id: item.id,
                    item_id: item.id,
                    sub_assembly_id: item.item?.id,
                    sub_assembly_no: item.item?.item_no,
                    sub_assembly_unit_of_measure_code: item.item?.unit_of_measure_code,
                    description: item.item?.description,
                    unit_price: item.unit_price,
                    qty: item.qty,
                    qty_per_assembly: item.qty_per_assembly,
                    scrap: item.scrap,
                    routing_operation_id: item?.routing_operation_id ?? '',
                    actual_consumption: (item?.actual_consumption ?? []).map((row) => ({
                        id: row.id,
                        item_id: row.item_id,
                        item_no: row.item_no,
                        description: row.description,
                        quantity: row.quantity ?? row.qty ?? 0,
                        unit_of_measure_code: row.unit_of_measure_code,
                        notes: row.notes ?? ''
                    }))
                })))
                setoperations((data?.production_order_operations ?? []).map((op) => ({
                    id: op.id,
                    sequence: op.sequence,
                    operationName: op.operation_name,
                    workCenterId: op.work_center_id ?? '',
                    productionRate: op.production_rate ?? '',
                    rateUom: op.production_rate_uom ?? '',
                    setupTime: parseSetupTime(op.setup_time),
                    plannedTime: op.planned_time,
                    runTime: toNumber(op.production_rate) > 0 ? round(toNumber(data?.qty_to_produce) / toNumber(op.production_rate), 2) : ''
                })))
                setProductionReports(data?.production_order_actuals ?? [])

                if (!dueToPusherEvent) {
                    setComments('')
                }
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const buildProductionReportsPayload = () => productionReports.map((entry) => ({
        ...(entry.isNew ? {} : { id: entry.id }),
        operation_id: entry.operation_id,
        production_date: entry.production_date,
        good_qty: entry.good_qty,
        scrap_qty: entry.scrap_qty,
        scrap_reason: entry.scrap_reason,
        operators: (entry.operators ?? []).map((operator) => ({ employee_id: operator.employee_id, labor_hours: operator.labor_hours })),
        machine_hours: entry.machine_hours,
        downtime_hours: entry.downtime_hours,
        downtime_reason: entry.downtime_reason
    }))

    const saveProductionOrder = async () => {
        setIsLoading(true)
        const formData = {}

        formData['qty_produced'] = progressQty
        formData['comments'] = comments
        formData['production_reports'] = buildProductionReportsPayload()
        formData['material_consumption'] = buildMaterialConsumptionPayload()

        await axios
            .put(`/api/production-orders/${params.id}/save`, formData, config).then((data) => {
                Swal.fire({
                    icon: "success",
                    text: data.data.success.message,
                    customClass: 'success',
                    showCloseButton: true,
                    iconColor: '#00B78E'
                })
                setComments('')
                setIsEditingProgress(false)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const saveProductionOrderProgress = async () => {
        setIsLoading(true)
        const formData = {}

        formData['qty_produced'] = progressQty

        await axios
            .put(`/api/production-orders/${params.id}/save`, formData, config).then((data) => {
                Swal.fire({
                    icon: "success",
                    text: data.data.success.message,
                    customClass: 'success',
                    showCloseButton: true,
                    iconColor: '#00B78E'
                })
                setIsEditingProgress(false)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const completeProductionOrder = async () => {
        setIsLoading(true)
        const formData = {}

        formData['qty_produced'] = progressQty
        formData['comments'] = comments
        formData['production_reports'] = buildProductionReportsPayload()
        formData['material_consumption'] = buildMaterialConsumptionPayload()

        await axios
            .put(`/api/production-orders/${params.id}/complete`, formData, config).then((data) => {
                Swal.fire({
                    icon: "success",
                    text: data.data.success.message,
                    customClass: 'success',
                    showCloseButton: true,
                    iconColor: '#00B78E'
                })
                navigate('/production-order', {
                    replace: true
                })
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleDoNotSaveProgress = () => {
        setProgressQty(qtyProduced)
        setIsEditingProgress(false)
    }

    const handleClose = () => {
        if (
            !isEqual(qtyProduced, progressQty)
            || !isEmpty(comments)
        ) {
            Swal.fire({
                text: t('unsaved_changes_exit_warning'),
                icon: 'warning',
                customClass: 'warning2',
                iconColor: '#FEB806',
                reverseButtons: true,
                showDenyButton: true,
                showCloseButton: false,
                confirmButtonText: t('yes'),
                denyButtonText: t('negatory'),
                allowOutsideClick: false
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/production-order')
                }
            })
        } else {
            navigate('/production-order')
        }
    }

    const operationSelectOptions = operations.map((op) => ({
        id: op.id,
        label: `${op.sequence} - ${operationOptions.find(o => o.value === op.operationName)?.label ?? op.operationName} (${workCenters.find(wc => wc.id === op.workCenterId)?.work_center_name ?? ''})`
    }))

    const resetReportForm = () => {
        setReportOperationId('')
        setReportDate(dayjs())
        setReportGoodQty('')
        setReportScrapQty('')
        setReportScrapReason('')
        setReportOperators([])
        setReportMachineHours('')
        setReportDowntimeHours('')
        setReportDowntimeReason('')
    }

    const openReportModal = () => {
        resetReportForm()
        setEditingReportId(null)
        setReportModalOpen(true)
    }

    const handleEditReport = (row) => {
        setEditingReportId(row.id)
        setReportOperationId(row.operation_id ?? '')
        setReportDate(dayjs(row.production_date))
        setReportGoodQty(row.good_qty)
        setReportScrapQty(row.scrap_qty)
        setReportScrapReason(row.scrap_reason ?? '')
        setReportOperators((row.operators ?? []).map((operator) => ({ id: Date.now() + Math.random(), employeeId: operator.employee_id, hours: operator.labor_hours })))
        setReportMachineHours(row.machine_hours)
        setReportDowntimeHours(row.downtime_hours)
        setReportDowntimeReason(row.downtime_reason ?? '')
        setReportModalOpen(true)
    }

    const closeReportModal = () => {
        setReportModalOpen(false)
    }

    const deleteReport = async (row) => {
        const isConfirm = await Swal.fire({
            title: `${t('title_delete--long', { x: t('production_report').toLocaleLowerCase(), y: `${row.production_date} - ${row.operation_label}` })}`,
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => result.isConfirmed)

        if (!isConfirm) return

        setProductionReports(prev => prev.filter(entry => entry.id !== row.id))
    }

    const addReportOperator = () => {
        setReportOperators(prev => [...prev, { id: Date.now(), employeeId: '', hours: '' }])
    }

    const removeReportOperator = (id) => {
        setReportOperators(prev => prev.filter(row => row.id !== id))
    }

    const updateReportOperator = (id, field, value) => {
        setReportOperators(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row))
    }

    const isReportFormValid = () => (
        isValid(reportOperationId)
        && isValid(reportDate)
        && isValid(reportGoodQty)
    )

    const reportProduction = () => {
        const isEditing = isValid(editingReportId)
        const existingEntry = isEditing ? productionReports.find(row => row.id === editingReportId) : null

        const entry = {
            id: isEditing ? editingReportId : Date.now(),
            isNew: isEditing ? (existingEntry?.isNew ?? false) : true,
            operation_id: reportOperationId,
            operation_label: operationSelectOptions.find(o => o.id === reportOperationId)?.label ?? '',
            production_date: dayjs(reportDate).format('YYYY-MM-DD'),
            good_qty: toNumber(reportGoodQty),
            scrap_qty: toNumber(reportScrapQty) || 0,
            scrap_reason: reportScrapReason,
            operators: reportOperators
                .filter(row => isValid(row.employeeId))
                .map(row => ({
                    employee_id: row.employeeId,
                    employee_name: employees.find(e => e.id === row.employeeId)?.name ?? '',
                    labor_hours: toNumber(row.hours) || 0
                })),
            machine_hours: toNumber(reportMachineHours) || 0,
            downtime_hours: toNumber(reportDowntimeHours) || 0,
            downtime_reason: reportDowntimeReason
        }

        setProductionReports(prev => isEditing
            ? prev.map(row => row.id === editingReportId ? entry : row)
            : [entry, ...prev]
        )
        closeReportModal()
    }

    const productionActualsTotals = productionReports.reduce((totals, entry) => ({
        good_qty: totals.good_qty + toNumber(entry.good_qty),
        scrap_qty: totals.scrap_qty + toNumber(entry.scrap_qty),
        labor_hours: totals.labor_hours + (entry.operators ?? []).reduce((sum, row) => sum + toNumber(row.labor_hours), 0),
        machine_hours: totals.machine_hours + toNumber(entry.machine_hours),
        downtime_hours: totals.downtime_hours + toNumber(entry.downtime_hours)
    }), { good_qty: 0, scrap_qty: 0, labor_hours: 0, machine_hours: 0, downtime_hours: 0 })

    const getMaterialActualQty = (row) => (row.actual_consumption ?? []).reduce((sum, entry) => sum + toNumber(entry.quantity), 0)

    const getMaterialVariance = (row) => round(getMaterialActualQty(row) - (toNumber(row.qty_per_assembly) * toNumber(qtyToProduce)), 2)

    const getOperationLabel = (routingOperationId) => {
        const op = operations.find(o => o.id === routingOperationId)
        if (!op) return ''
        return `${op.sequence} - ${operationOptions.find(o => o.value === op.operationName)?.label ?? op.operationName} (${workCenters.find(wc => wc.id === op.workCenterId)?.work_center_name ?? ''})`
    }

    const openMaterialConsumptionModal = (row) => {
        setSelectedMaterialRow(row)
        setConsumptionRows((row.actual_consumption ?? []).map((entry) => ({
            id: entry.id,
            itemId: entry.item_id,
            quantity: entry.quantity,
            uom: entry.unit_of_measure_code,
            notes: entry.notes ?? '',
            isNew: false
        })))
        setMaterialConsumptionModalOpen(true)
    }

    const closeMaterialConsumptionModal = () => {
        setMaterialConsumptionModalOpen(false)
        setSelectedMaterialRow(null)
    }

    const addConsumptionRow = () => {
        setConsumptionRows(prev => [...prev, { id: Date.now(), itemId: '', quantity: '', uom: '', notes: '', isNew: true }])
    }

    const removeConsumptionRow = (id) => {
        setConsumptionRows(prev => prev.filter(row => row.id !== id))
    }

    const updateConsumptionRow = (id, field, value) => {
        setConsumptionRows(prev => prev.map(row => {
            if (row.id !== id) return row
            if (field === 'itemId') {
                const selectedItem = allItems.find(i => i.id === value)
                return { ...row, itemId: value, uom: selectedItem?.unit_of_measure_name ?? selectedItem?.unit_of_measure_code ?? row.uom }
            }
            return { ...row, [field]: value }
        }))
    }

    const saveMaterialConsumption = () => {
        const entries = consumptionRows
            .filter(row => isValid(row.itemId))
            .map(row => ({
                ...(row.isNew ? {} : { id: row.id }),
                isNew: row.isNew,
                item_id: row.itemId,
                item_no: allItems.find(i => i.id === row.itemId)?.item_no ?? '',
                description: allItems.find(i => i.id === row.itemId)?.description ?? '',
                quantity: toNumber(row.quantity) || 0,
                unit_of_measure_code: row.uom,
                notes: row.notes
            }))

        setMaterials(prev => prev.map(row => row.id === selectedMaterialRow.id ? { ...row, actual_consumption: entries } : row))
        closeMaterialConsumptionModal()
    }

    const buildMaterialConsumptionPayload = () => materials
        .filter(row => (row.actual_consumption ?? []).length > 0)
        .map(row => ({
            production_order_item_id: row.id,
            actual_consumption: row.actual_consumption.map((entry) => ({
                ...(entry.isNew ? {} : { id: entry.id }),
                item_id: entry.item_id,
                quantity: entry.quantity,
                unit_of_measure_code: entry.unit_of_measure_code,
                notes: entry.notes
            }))
        }))

    const materialsColumns = [
        {
            field: 'sub_assembly_no',
            headerName: t('item_number_abbr'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialNo params={params} t={t} />
        },
        {
            field: 'description',
            headerName: t('description'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialDescription params={params} t={t} />
        },
        {
            field: 'routing_operation_id',
            headerName: t('operation'),
            flex: 1,
            minWidth: 170,
            renderCell: (params) => <MaterialOperation params={params} t={t} operations={operations} operationOptions={operationOptions} workCenters={workCenters} />
        },
        {
            field: 'qty_per_assembly',
            headerName: t('qty_per_assembly'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialQtyPerAssembly params={params} t={t} />
        },
        {
            field: 'qty',
            headerName: t('totalQty'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialQty params={params} t={t} qtyToProduce={qtyToProduce} />,
            valueGetter: (params) => params.row.qty_per_assembly * qtyToProduce,
            sortComparator: numericSortComparator
        },
        {
            field: 'sub_assembly_unit_of_measure_code',
            headerName: t('unit_of_measure'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialUnitOfMeasure params={params} t={t} />
        },
        {
            field: 'scrap',
            headerName: `${t('scrap')} (%)`,
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialScrap params={params} t={t} />
        },
        {
            field: 'actual_qty',
            headerName: t('actual_qty'),
            flex: 1,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => <OperationField value={getMaterialActualQty(params.row)} />
        },
        {
            field: 'variance',
            headerName: t('variance'),
            flex: 1,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => {
                const variance = getMaterialVariance(params.row)
                return <OperationField value={variance} color={variance < 0 ? '#C0362C' : variance > 0 ? '#B5790A' : undefined} />
            }
        },
        {
            field: 'material_consumption_action',
            headerName: t('actions'),
            sortable: false,
            flex: 0.8,
            minWidth: 90,
            renderCell: (params) => {
                const hasEntries = (params.row.actual_consumption ?? []).length > 0
                return (
                    <Tooltip disableInteractive title={hasEntries ? t('update') : t('enter')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => openMaterialConsumptionModal(params.row)}>
                                <i className={`fa-solid ${hasEntries ? 'fa-pencil' : 'fa-circle-plus'}`}></i>
                            </span>
                        </div>
                    </Tooltip>
                )
            }
        }
    ]

    const operationsColumns = [
        { field: 'sequence', headerName: t('seq'), flex: 0.5, minWidth: 90, renderCell: (params) => <OperationField value={params.row.sequence} /> },
        {
            field: 'operationName',
            headerName: t('operation'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => <OperationField value={operationOptions.find(o => o.value === params.row.operationName)?.label ?? params.row.operationName} />
        },
        {
            field: 'workCenterName',
            headerName: t('work_center'),
            flex: 1,
            minWidth: 170,
            renderCell: (params) => <OperationField value={workCenters.find(wc => wc.id === params.row.workCenterId)?.work_center_name ?? ''} />
        },
        { field: 'productionRate', headerName: t('production_rate'), flex: 0.8, minWidth: 110, renderCell: (params) => <OperationField value={params.row.productionRate} /> },
        {
            field: 'rateUom',
            headerName: t('rate_uom'),
            flex: 0.9,
            minWidth: 150,
            renderCell: (params) => <OperationField value={rateUomOptions.find(o => o.value === params.row.rateUom)?.label ?? params.row.rateUom} />
        },
        {
            field: 'runTime',
            headerName: t('run_time'),
            flex: 0.7,
            minWidth: 110,
            renderCell: (params) => <OperationField value={isValid(params.row.runTime) ? `${params.row.runTime} h` : ''} />
        },
        { field: 'setupTime', headerName: t('setup_minutes'), flex: 0.7, minWidth: 110, renderCell: (params) => <OperationField value={params.row.setupTime} /> },
        {
            field: 'plannedTime',
            headerName: t('planned_time'),
            flex: 0.7,
            minWidth: 120,
            renderCell: (params) => <OperationField value={isValid(params.row.plannedTime) ? `${params.row.plannedTime} h` : ''} />
        },
        {
            field: 'availableHoursPerDay',
            headerName: t('available_hours'),
            flex: 0.7,
            minWidth: 130,
            renderCell: (params) => {
                const hoursPerDay = workCenters.find(wc => wc.id === params.row.workCenterId)?.available_hours
                return <OperationField value={isValid(hoursPerDay) ? `${hoursPerDay} h` : ''} />
            }
        },
        {
            field: 'estimatedProductionDays',
            headerName: t('estimated_production_days'),
            flex: 0.7,
            minWidth: 150,
            renderCell: (params) => {
                const hoursPerDay = toNumber(workCenters.find(wc => wc.id === params.row.workCenterId)?.available_hours)
                const days = hoursPerDay > 0 ? round(toNumber(params.row.plannedTime) / hoursPerDay, 2) : ''
                return <OperationField value={days !== '' ? `${days} ${t('days')}` : ''} />
            }
        }
    ]

    const reportsColumns = [
        { field: 'production_date', headerName: t('date'), flex: 0.8, minWidth: 110 },
        { field: 'operation_label', headerName: t('operation'), flex: 1.2, minWidth: 170 },
        { field: 'good_qty', headerName: t('good'), flex: 0.7, minWidth: 90, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'scrap_qty', headerName: t('scrap'), flex: 0.7, minWidth: 90, valueFormatter: ({ value }) => value?.toLocaleString() },
        { field: 'machine_hours', headerName: t('machine_hours_abbr'), flex: 0.7, minWidth: 100 },
        { field: 'downtime_hours', headerName: t('downtime'), flex: 0.7, minWidth: 100 },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.6,
            minWidth: 100,
            renderCell: (params) => (
                <div className='flex justify-between'>
                    <Tooltip disableInteractive title={t('update')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => handleEditReport(params.row)}><i className="fa-solid fa-pencil"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteReport(params.row)}><i className="fa-solid fa-trash"></i></span>
                        </div>
                    </Tooltip>
                </div>
            )
        }
    ]

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box
                style={{
                    backgroundColor: "#F5F5FD",
                    minHeight: '100vh'
                }}
            >
                {/* HEADER */}
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex flex-wrap gap-3 items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#1ab800] text-[35px] mr-4'>•</span> {productionOrderNo} <span className='mx-3 text-[#D4D4D4] font-light'>|</span> <span className='font-normal'>{statusName}</span> </p>

                        {isEditingProgress ? (
                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='center'
                                justifyContent='end'
                                height='41px'
                                flexGrow='1'
                            >
                                <TextField
                                    sx={{
                                        width: '280px',
                                        background: '#FCFCFC',
                                        '.MuiOutlinedInput-input': {
                                            padding: '9px !important'
                                        },
                                        boxShadow: 'none',
                                        '.MuiOutlinedInput-notchedOutline': {
                                            border: '1px solid #EEEFF2 !important'
                                        }
                                    }}
                                    type="text"
                                    name="qty_produced"
                                    label={t('quantity_produced')}
                                    value={progressQty}
                                    onChange={(e) => handleInteger({
                                        event: e,
                                        setter: setProgressQty,
                                        upperLimit: qtyToProduce,
                                        name: t('quantity_produced')
                                    })}
                                    onBlur={e => handleZero({
                                        event: e,
                                        setter: setProgressQty
                                    })}
                                    onFocus={e => deleteZero({
                                        event: e,
                                        setter: setProgressQty,
                                        value: progressQty
                                    })}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">/ {qtyToProduce}</InputAdornment>
                                    }}
                                />

                                <button
                                    type="button"
                                    onClick={handleDoNotSaveProgress}
                                    className={`h-[41px] text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-red-600 hover:bg-red-800 disabled:bg-red-400`}
                                    disabled={statusId !== 2}
                                >
                                    <i className="fa-solid fa-rotate-left"></i>
                                </button>

                                <button
                                    type="button"
                                    onClick={saveProductionOrderProgress}
                                    className={`w-[117px] h-[41px] text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#6C6C6C] hover:bg-[#565656] disabled:bg-[#989898]`}
                                    disabled={statusId !== 2}
                                >
                                    {t('save')}
                                </button>
                            </Stack>
                        ) : (
                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='center'
                                justifyContent='end'
                                height='41px'
                                flexGrow='1'
                            >
                                <LinearProgress
                                    variant='determinate'
                                    value={progressPct > 100 ? 100 : progressPct}
                                    sx={{
                                        width: '205px',
                                        height: '18px',
                                        borderRadius: '9px',
                                        backgroundColor: "#EDEDED",
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: "#71DD37"
                                        }
                                    }}
                                />

                                <Typography fontSize='0.875rem'>
                                    {progressPct}%
                                </Typography>

                                <Tooltip title={statusId !== 2 ? '' : t('record_quantity_produced')}>
                                    <span>
                                        <button
                                            type="button"
                                            onClick={() => setIsEditingProgress(true)}
                                            disabled={statusId !== 2}
                                            className={`${statusId !== 2 ? 'hidden' : 'block'}`}
                                        >
                                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200">
                                                <i className="fa-solid fa-pencil"></i>
                                            </span>
                                        </button>
                                    </span>
                                </Tooltip>
                            </Stack>
                        )}

                        <div className="font-[600] text-[#333333]">
                            <button onClick={handleClose}>
                                <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200">
                                    <i className="fa-solid fa-xmark"></i>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* BODY */}
                <div className='p-5 pb-12'>
                    <div className='flex flex-col lg:flex-row gap-5'>
                        <div className='flex flex-col w-full lg:w-1/2 gap-5'>
                            {/* GENERAL INFORMATION section */}
                            <div
                                className='bg-white p-6 pb-[12px] pt-0 rounded-md w-full h-fit w-full'
                                style={{
                                    boxShadow: `0px 1px 2px 0px #1018280F, 0px 1px 2px 0px #1018280F, 0px -1px 4px 0px #1018280F`
                                }}
                            >
                                <div className='pb-6 pt-1 flex gap-3 items-center'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>
                                        {t('general_information')}
                                    </p>
                                </div>

                                <Stack direction='column' width='100%'>
                                    {/* Production Order No. */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('production_order_no')}</p>
                                        <div className='w-full'>
                                            <TextField
                                                sx={{
                                                    width: '100%',
                                                    background: '#FCFCFC',
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '9px !important'
                                                    },
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid #EEEFF2 !important'
                                                    }
                                                }}
                                                type="text"
                                                name="production_order_no"
                                                value={productionOrderNo}
                                                disabled
                                            />
                                        </div>
                                    </div>

                                    {/* Production Order Description */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('production_order_description')}</p>
                                        <div className='w-full'>
                                            <TextField
                                                sx={{
                                                    width: '100%',
                                                    background: '#FCFCFC',
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '9px !important'
                                                    },
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid #EEEFF2 !important'
                                                    }
                                                }}
                                                type="text"
                                                name="production_order_description"
                                                value={productionOrderDescription}
                                                disabled
                                            />
                                        </div>
                                    </div>

                                    {/* Item To Produce */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('item_to_produce')}</p>
                                        <div className='w-full'>
                                            <TextField
                                                sx={{
                                                    width: '100%',
                                                    background: '#FCFCFC',
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '9px !important'
                                                    },
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid #EEEFF2 !important'
                                                    }
                                                }}
                                                type="text"
                                                name="item_to_produce_no"
                                                value={`${itemNo} - ${itemDescription}`}
                                                disabled
                                            />
                                        </div>
                                    </div>

                                    <div className='w-full pb-4 lg:flex lg:gap-3'>
                                        {/* Quantity To Produce */}
                                        <div className='w-full pb-4 lg:w-1/2 lg:pb-0'>
                                            <p className="text-[14px] text-[#718096]">{t('quantity_to_produce')}</p>
                                            <div className='w-full'>
                                                <TextField
                                                    sx={{
                                                        width: '100%',
                                                        background: '#FCFCFC',
                                                        '.MuiOutlinedInput-root': {
                                                            borderTopRightRadius: '0px',
                                                            borderBottomRightRadius: '0px',
                                                        },
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '9px !important'
                                                        },
                                                        boxShadow: 'none',
                                                        '.MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid #EEEFF2 !important'
                                                        }
                                                    }}
                                                    type="text"
                                                    name="quantity_to_produce"
                                                    value={qtyToProduce}
                                                    disabled
                                                />
                                            </div>
                                        </div>

                                        {/* Grade Cocde */}
                                        <div className='w-full lg:w-1/2'>
                                            <p className="text-[14px] text-[#718096]">{t('grade_code')}</p>
                                            <div className='w-full'>
                                                <TextField
                                                    sx={{
                                                        width: '100%',
                                                        background: '#FCFCFC',
                                                        '.MuiOutlinedInput-root': {
                                                            borderTopLeftRadius: '0px',
                                                            borderBottomLeftRadius: '0px'
                                                        },
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '9px !important'
                                                        },
                                                        boxShadow: 'none',
                                                        '.MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid #EEEFF2 !important'
                                                        }
                                                    }}
                                                    type="text"
                                                    name="unit_of_measure"
                                                    value={itemGradeCodeCode ?? ''}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Stack>
                            </div>

                            {/* REPORT PRODUCTION section */}
                            <div
                                className='bg-white p-6 pb-[12px] pt-0 rounded-md w-full h-fit w-full'
                                style={{
                                    boxShadow: `0px 1px 2px 0px #1018280F, 0px 1px 2px 0px #1018280F, 0px -1px 4px 0px #1018280F`
                                }}
                            >
                                <div className='pb-6 pt-1'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>
                                        {t('report_production')}
                                    </p>
                                </div>

                                <div className='grid grid-cols-5 gap-3 pb-5'>
                                    <ReportStat label={t('good_qty')} value={productionActualsTotals.good_qty.toLocaleString()} />
                                    <ReportStat label={t('scrap_qty')} value={productionActualsTotals.scrap_qty.toLocaleString()} />
                                    <ReportStat label={t('labor_hours')} value={productionActualsTotals.labor_hours.toFixed(1)} />
                                    <ReportStat label={t('machine_hours')} value={productionActualsTotals.machine_hours.toFixed(1)} />
                                    <ReportStat label={t('downtime')} value={productionActualsTotals.downtime_hours.toFixed(1)} />
                                </div>

                                <div className='flex justify-between items-center pb-2'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('recent_entries')}</p>
                                    <Tooltip disableInteractive title={statusId !== 2 ? t('You must first start the order to begin making changes.') : t('add_production_report')} placement='bottom'>
                                        <div>
                                            <AddButton onClick={openReportModal} disabled={statusId !== 2}><i className="fa-solid fa-plus"></i></AddButton>
                                        </div>
                                    </Tooltip>
                                </div>
                                <div className='border rounded'>
                                    <Materials items={productionReports} columns={reportsColumns} />
                                </div>
                            </div>
                        </div>

                        <div className='flex flex-col w-full lg:w-1/2 gap-5'>
                            {/* MATERIAL LIST / OPERATIONS section */}
                            <div
                                className='bg-white p-6 pb-[12px] pt-0 rounded-md w-full h-fit w-full'
                                style={{
                                    boxShadow: `0px 1px 2px 0px #1018280F, 0px 1px 2px 0px #1018280F, 0px -1px 4px 0px #1018280F`
                                }}
                            >
                                <TabContext value={tabValue}>
                                    <TabList onChange={(e, value) => setTabValue(value)} aria-label="production order tabs">
                                        <Tab sx={{ textTransform: 'none' }} value="material" label={t('material_list')} icon={<i className="fa-solid fa-boxes-stacked"></i>} iconPosition='start' />
                                        <Tab sx={{ textTransform: 'none' }} value="operations" label={t('operations')} icon={<i className="fa-solid fa-gears"></i>} iconPosition='start' />
                                    </TabList>

                                    <TabPanel value="material" sx={{ padding: 0 }}>
                                        <div className='border-b border-l border-r rounded'>
                                            <Materials items={materials} columns={materialsColumns} random={random} />
                                        </div>

                                        <div className='flex justify-end w-full p-5'>
                                            <div className='flex flex-col gap-y-4 w-full lg:w-1/2 2xl:w-1/3'>
                                                {/* <div className='flex justify-between items-center'>
                                                    <p className="font-[400] text-[#88909C] text-[14px]">{t('total_cost')}</p>
                                                    <p className='font-[500] text-[16px]'>{currencySymbol}{totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                                </div> */}
                                                <div className='flex justify-between items-center'>
                                                    <p className="font-[400] text-[#88909C] text-[14px]">{t('totalQty')}</p>
                                                    <p className='font-[500] text-[16px]'>{totalQty}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </TabPanel>

                                    <TabPanel value="operations" sx={{ padding: 0 }}>
                                        <div className='border-b border-l border-r rounded'>
                                            <Materials items={operations} columns={operationsColumns} random={random} />
                                        </div>
                                    </TabPanel>
                                </TabContext>
                            </div>

                            {/* COMPLETION section */}
                            <div
                                className='bg-white p-6 pb-[12px] pt-0 rounded-md w-full h-fit w-full'
                                style={{
                                    boxShadow: `0px 1px 2px 0px #1018280F, 0px 1px 2px 0px #1018280F, 0px -1px 4px 0px #1018280F`
                                }}
                            >
                                <div className='pb-6 pt-1'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>
                                        {t('completion')}
                                    </p>
                                </div>

                                <Stack direction='column' width='100%'>
                                    <div className='lg:flex gap-3 w-full'>
                                        {/* Start Date */}
                                        <div className='w-full lg:w-1/2 pb-4'>
                                            <p className="text-[14px] text-[#718096]">{t('start_date')}</p>
                                            <div className='w-full'>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <MobileDatePicker
                                                        sx={{
                                                            width: '100%',
                                                            background: '#FCFCFC',
                                                            '.MuiOutlinedInput-input': {
                                                                padding: '9px !important'
                                                            },
                                                            boxShadow:
                                                                'none',
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                border: '1px solid #EEEFF2 !important'
                                                            }
                                                        }}
                                                        format="YYYY-MM-DD"
                                                        value={startDate}
                                                        disabled
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                        </div>

                                        {/* Start Time */}
                                        <div className='w-full lg:w-1/2 pb-4'>
                                            <p className="text-[14px] text-[#718096]">{t('start_time')}</p>
                                            <div className='w-full'>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DemoContainer sx={{ padding: '0' }} components={['TimePicker', 'TimePicker']}>
                                                        <MobileTimePicker
                                                            sx={{
                                                                width: '100%',
                                                                background: '#FCFCFC',
                                                                '.MuiOutlinedInput-input': {
                                                                    padding: '9px !important'
                                                                },
                                                                boxShadow: 'none',
                                                                '.MuiOutlinedInput-notchedOutline': {
                                                                    border: '1px solid #EEEFF2 !important'
                                                                }
                                                            }}
                                                            value={startTime}
                                                            disabled
                                                        />
                                                    </DemoContainer>
                                                </LocalizationProvider>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Due Date */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('due_date')}</p>
                                        <div className='w-full'>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <MobileDatePicker
                                                    sx={{
                                                        width: '100%',
                                                        background: '#FCFCFC',
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '9px !important'
                                                        },
                                                        boxShadow: 'none',
                                                        '.MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid #EEEFF2 !important'
                                                        }
                                                    }}
                                                    format="YYYY-MM-DD"
                                                    value={dueDate}
                                                    disabled
                                                />
                                            </LocalizationProvider>
                                        </div>
                                    </div>
                                </Stack>
                            </div>

                            {/* COMMENTS section */}
                            <div
                                className='bg-white p-6 pb-[12px] pt-0 rounded-md w-full h-fit w-full'
                                style={{
                                    boxShadow: `0px 1px 2px 0px #1018280F, 0px 1px 2px 0px #1018280F, 0px -1px 4px 0px #1018280F`
                                }}
                            >
                                <div className='pb-3 pt-1'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>
                                        {t('comments')}
                                    </p>
                                </div>

                                <div className='w-full pb-4'>
                                    <div className='flex justify-between items-center'>
                                        <p className="text-[14px] text-[#718096]">{t('comments_text')}</p>
                                        <CommentHistoryButton
                                            badgeContent={commentHistory?.length ?? ''}
                                            backgroundColor='#1ab800'
                                            onClick={() => setOpenCommentHistory(true)}
                                        />
                                    </div>

                                    <TextField
                                        disabled={statusId !== 2}
                                        multiline
                                        rows={3}
                                        value={comments}
                                        onChange={e => setComments(e.target.value)}
                                        sx={{
                                            width: '100%',
                                            background: '#FCFCFC',
                                            '.MuiOutlinedInput-input': {
                                                padding: '9px !important'
                                            },
                                            boxShadow: 'none',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                border: '1px solid #EEEFF2 !important'
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SAVE & COMPLETE section */}
                    <div className='w-full h-fit mt-5'>
                        <div className='w-full flex justify-end gap-3'>
                            <Tooltip disableInteractive title={statusId !== 2 ? t('You must first start the order to begin making changes.') : ''} placement='left'>
                                <span>
                                    <button
                                        type="button"
                                        onClick={saveProductionOrder}
                                        className='w-[117px] h-[41px] text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#6C6C6C] hover:bg-[#565656] disabled:bg-[#989898]'
                                        disabled={statusId !== 2}
                                    >
                                        {t('save')}
                                    </button>
                                </span>
                            </Tooltip>

                            <Tooltip disableInteractive title={statusId !== 2 ? t('You must first start the order to begin making changes.') : ''}>
                                <span>
                                    <button
                                        type="button"
                                        onClick={completeProductionOrder}
                                        className='h-[41px] text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#00C448] hover:bg-[#019738] disabled:bg-[#B8B7BC]'
                                        disabled={statusId !== 2}
                                    >
                                        <span className='pr-2'><i className="fa-solid fa-check"></i></span> {t('complete')}
                                    </button>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </Box>
            <CommentHistoryModal
                open={openCommentHistory}
                setOpen={setOpenCommentHistory}
                comments={commentHistory}
            />

            {/* Report Production modal */}
            <Modal open={reportModalOpen} onClose={closeReportModal}>
                <Box sx={reportModalStyle}>
                    <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#336195', borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-baseline justify-between w-full'>
                            <p className='text-[18px] font-[600] text-white'>{editingReportId ? t('edit_production_report') : t('report_production')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                                <button onClick={closeReportModal}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>

                    <div className='p-5'>
                        <div className='lg:flex gap-3 w-full'>
                            {/* Production Order */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('production_order_no')}</p>
                                <TextField sx={TEXTFIELD_SX} type="text" value={productionOrderNo} disabled />
                            </div>

                            {/* Operation */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('operation')} *</p>
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    options={operationSelectOptions}
                                    value={reportOperationId ? (operationSelectOptions.find(o => o.id === reportOperationId) ?? null) : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(e, value) => setReportOperationId(value?.id ?? '')}
                                    sx={{ width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' }, boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' } }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Production Date */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('production_date')} *</p>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker
                                        sx={{ ...TEXTFIELD_SX, width: '100%' }}
                                        format="YYYY-MM-DD"
                                        value={reportDate}
                                        onChange={(newValue) => setReportDate(newValue)}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>

                        <div className='lg:flex gap-3 w-full'>
                            {/* Good Quantity */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('good_quantity')} *</p>
                                <TextField
                                    sx={TEXTFIELD_SX}
                                    type="text"
                                    value={reportGoodQty}
                                    onChange={(e) => handleInteger({ event: e, setter: setReportGoodQty, lowerLimit: 0, name: t('good_quantity') })}
                                />
                            </div>

                            {/* Scrap Quantity */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('scrap_quantity')}</p>
                                <TextField
                                    sx={TEXTFIELD_SX}
                                    type="text"
                                    value={reportScrapQty}
                                    onChange={(e) => handleInteger({ event: e, setter: setReportScrapQty, lowerLimit: 0, name: t('scrap_quantity') })}
                                />
                            </div>

                            {/* Scrap Reason */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('scrap_reason')}</p>
                                <TextField sx={TEXTFIELD_SX} type="text" value={reportScrapReason} onChange={(e) => setReportScrapReason(e.target.value)} />
                            </div>
                        </div>

                        {/* Operators / Labor Hours */}
                        <div className='pb-4'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8] pb-2'>{t('operators_labor_hours')}</p>
                            <div className='border border-[#E5E7EB] rounded-md overflow-hidden'>
                                <div className='grid grid-cols-5 gap-2 px-4 py-2 text-[12px] font-[600] text-[#88909C] border-b border-[#E5E7EB] bg-[#FAFAFA]'>
                                    <span className='col-span-2'>{t('operator')}</span>
                                    <span className='col-span-2'>{t('labor_hours')}</span>
                                    <span>{t('actions')}</span>
                                </div>
                                {reportOperators.map((row) => (
                                    <div key={row.id} className='grid grid-cols-5 gap-2 px-4 py-2 items-center border-t border-[#E5E7EB]'>
                                        <div className='col-span-2'>
                                            <Autocomplete
                                                disablePortal
                                                disableClearable
                                                options={employees}
                                                getOptionLabel={(option) => option.name ?? ''}
                                                value={employees.find(e => e.id === row.employeeId) ?? null}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                onChange={(e, value) => updateReportOperator(row.id, 'employeeId', value?.id ?? '')}
                                                sx={{ width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' }, boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' } }}
                                                renderInput={(params) => <TextField {...params} size='small' />}
                                            />
                                        </div>
                                        <div className='col-span-2'>
                                            <TextField
                                                sx={TEXTFIELD_SX}
                                                size='small'
                                                type="text"
                                                value={row.hours}
                                                onChange={(e) => updateReportOperator(row.id, 'hours', e.target.value)}
                                            />
                                        </div>
                                        <Tooltip disableInteractive title={t('remove')} placement='bottom'>
                                            <div style={{ color: 'rgba(0,0,0,.54)' }}>
                                                <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => removeReportOperator(row.id)}><i className="fa-solid fa-trash"></i></span>
                                            </div>
                                        </Tooltip>
                                    </div>
                                ))}
                            </div>
                            <button
                                type='button'
                                onClick={addReportOperator}
                                className='mt-3 rounded-md py-2 px-4 text-[13px] font-[600]'
                                style={{ border: '1px solid #EEEFF2', color: '#336195' }}
                            >
                                <i className="fa-solid fa-plus pr-1"></i> {t('add_operator')}
                            </button>
                        </div>

                        <div className='lg:flex gap-3 w-full'>
                            {/* Machine Hours */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('machine_hours')}</p>
                                <TextField sx={TEXTFIELD_SX} type="text" value={reportMachineHours} onChange={(e) => setReportMachineHours(e.target.value)} />
                            </div>

                            {/* Downtime Hours */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('downtime_hours')}</p>
                                <TextField sx={TEXTFIELD_SX} type="text" value={reportDowntimeHours} onChange={(e) => setReportDowntimeHours(e.target.value)} />
                            </div>

                            {/* Downtime Reason */}
                            <div className='w-full lg:w-1/3 pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('downtime_reason')}</p>
                                <TextField sx={TEXTFIELD_SX} type="text" value={reportDowntimeReason} onChange={(e) => setReportDowntimeReason(e.target.value)} />
                            </div>
                        </div>

                        <div className='w-full flex justify-end gap-3 pt-2'>
                            <button
                                type='button'
                                onClick={closeReportModal}
                                className='text-[#919399] font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#F3F5F7]'
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type='button'
                                disabled={!isReportFormValid()}
                                onClick={reportProduction}
                                className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#00C448] hover:bg-[#019738] disabled:bg-[#B8B7BC]'
                            >
                                {t('save')}
                            </button>
                        </div>
                    </div>
                </Box>
            </Modal>

            {/* Record Material Consumption modal */}
            <Modal open={materialConsumptionModalOpen} onClose={closeMaterialConsumptionModal}>
                <Box sx={reportModalStyle}>
                    <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#336195', borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-baseline justify-between w-full'>
                            <p className='text-[18px] font-[600] text-white'>{selectedMaterialRow?.sub_assembly_no} - {selectedMaterialRow?.description} {t('record_material_consumption')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                                <button onClick={closeMaterialConsumptionModal}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>

                    <div className='p-5'>
                        <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit mb-5 w-full'>
                            <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('planned_material')}</p>
                            <div className='grid grid-cols-2 divide-x divide-[#E5E7EB] border-t border-[#E5E7EB]'>
                                <div className='divide-y divide-[#E5E7EB]'>
                                    <DetailField label={t('item')} value={`${selectedMaterialRow?.sub_assembly_no} - ${selectedMaterialRow?.description}`} />
                                    <DetailField label={t('planned_quantity')} value={`${round(toNumber(selectedMaterialRow?.qty_per_assembly) * toNumber(qtyToProduce), 2)} ${selectedMaterialRow?.sub_assembly_unit_of_measure_code ?? ''}`} />
                                </div>
                                <div className='divide-y divide-[#E5E7EB]'>
                                    <DetailField label={t('operation')} value={getOperationLabel(selectedMaterialRow?.routing_operation_id)} />
                                    <DetailField label={`${t('scrap')} (%)`} value={selectedMaterialRow?.scrap} />
                                </div>
                            </div>
                        </div>

                        <p className='text-[13px] font-[600] uppercase text-[#A1ACB8] pb-2'>{t('actual_material_consumption')}</p>
                        <div className='border border-[#E5E7EB] rounded-md overflow-hidden'>
                            <div className='grid grid-cols-12 gap-2 px-4 py-2 text-[12px] font-[600] text-[#88909C] border-b border-[#E5E7EB] bg-[#FAFAFA]'>
                                <span className='col-span-4'>{t('actual_item')}</span>
                                <span className='col-span-2'>{t('quantity')}</span>
                                <span className='col-span-2'>{t('uom')}</span>
                                <span className='col-span-3'>{t('notes')}</span>
                                <span className='col-span-1'>{t('actions')}</span>
                            </div>
                            {consumptionRows.map((row) => (
                                <div key={row.id} className='grid grid-cols-12 gap-2 px-4 py-2 items-center border-t border-[#E5E7EB]'>
                                    <div className='col-span-4'>
                                        <Autocomplete
                                            disablePortal
                                            disableClearable
                                            options={allItems}
                                            getOptionLabel={(option) => option.item_no ? `${option.item_no} - ${option.description ?? ''}` : ''}
                                            value={allItems.find(i => i.id === row.itemId) ?? null}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(e, value) => updateConsumptionRow(row.id, 'itemId', value?.id ?? '')}
                                            sx={{ width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' }, boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' } }}
                                            renderInput={(params) => <TextField {...params} size='small' />}
                                        />
                                    </div>
                                    <div className='col-span-2'>
                                        <TextField sx={TEXTFIELD_SX} size='small' type="text" value={row.quantity} onChange={(e) => updateConsumptionRow(row.id, 'quantity', e.target.value)} />
                                    </div>
                                    <div className='col-span-2'>
                                        <TextField sx={TEXTFIELD_SX} size='small' type="text" value={row.uom} disabled />
                                    </div>
                                    <div className='col-span-3'>
                                        <TextField sx={TEXTFIELD_SX} size='small' type="text" value={row.notes} onChange={(e) => updateConsumptionRow(row.id, 'notes', e.target.value)} />
                                    </div>
                                    <div className='col-span-1'>
                                        <Tooltip disableInteractive title={t('remove')} placement='bottom'>
                                            <div style={{ color: 'rgba(0,0,0,.54)' }}>
                                                <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => removeConsumptionRow(row.id)}><i className="fa-solid fa-trash"></i></span>
                                            </div>
                                        </Tooltip>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='mt-3'>
                            <Tooltip disableInteractive title={t('add_new_material')} placement='bottom'>
                                <div>
                                    <AddButton onClick={addConsumptionRow}><i className="fa-solid fa-plus"></i></AddButton>
                                </div>
                            </Tooltip>
                        </div>

                        <div className='w-full flex justify-end gap-3 pt-5'>
                            <button
                                type='button'
                                onClick={closeMaterialConsumptionModal}
                                className='text-[#919399] font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#F3F5F7]'
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type='button'
                                onClick={saveMaterialConsumption}
                                className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#015D9F] hover:bg-[#01477d]'
                            >
                                {t('save')}
                            </button>
                        </div>
                    </div>
                </Box>
            </Modal>
        </>
    )
}

export default ProcessProductionOrder

const OperationField = ({ value, color }) => {
    return (
        <TextField
            type="text"
            variant='standard'
            disabled
            className='w-full mb-5 px-0 pt-0'
            value={value}
            sx={color ? { '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: color, color } } : undefined}
        />
    )
}

const MaterialNo = ({ params, t }) => {
    return (
        <TextField
            type="text"
            variant='standard'
            disabled
            className='w-full mb-5 px-0 pt-0'
            value={params.value}
        />
    )
}

const MaterialDescription = ({ params, t }) => {
    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={params.value}
            disabled
        />
    )
}

const MaterialOperation = ({ params, t, operations, operationOptions, workCenters }) => {
    const op = operations.find(o => o.id === params.value)
    const label = op
        ? `${op.sequence} - ${operationOptions.find(o => o.value === op.operationName)?.label ?? op.operationName} (${workCenters.find(wc => wc.id === op.workCenterId)?.work_center_name ?? ''})`
        : ''

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={label}
            disabled
        />
    )
}

const MaterialQtyPerAssembly = ({ params, t }) => {
    const [qty, setQty] = useState(params.value)

    return (
        <TextField
            disabled
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={qty}
        />
    )
}

const MaterialQty = ({ params, t, qtyToProduce }) => {
    const [qty, setQty] = useState(params.value)

    useEffect(() => {
        setQty(params.row.qty_per_assembly * qtyToProduce)
    }, [params.row.qty_per_assembly, qtyToProduce])

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={qty}
            disabled
        />
    )
}

const MaterialUnitOfMeasure = ({ params, t }) => {
    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={params.value}
            disabled
        />
    )
}

const MaterialScrap = ({ params, t, }) => {
    const [scrap, setScrap] = useState(params.value)

    return (
        <TextField
            disabled
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={scrap}
        />
    )
}

const MaterialUnitPrice = ({ params, t, currencySymbol }) => {
    const [unitPrice, setUnitPrice] = useState(params.value)

    useEffect(() => {
        setUnitPrice(params.value)
    }, [params.value])

    return (
        <div className='flex items-center'>
            <span style={{ lineHeight: '32px', padding: '4px 0 5px', color: 'rgba(0, 0, 0, 0.38)' }}>{currencySymbol}</span>
            <TextField
                disabled
                type="text"
                variant='standard'
                className='w-full mb-5 px-0 pt-0'
                value={unitPrice}
            />
        </div>
    )
}

const MaterialTotalPrice = ({ params, t, currencySymbol, calculateTotalCost, qtyToProduce }) => {
    const [totalPrice, setTotalPrice] = useState(0)

    useEffect(() => {
        setTotalPrice(round((params.row.qty_per_assembly * params.row.unit_price) * qtyToProduce, 2))
        calculateTotalCost()
    }, [params.row.qty_per_assembly, params.row.unit_price, qtyToProduce])

    return (
        <>{currencySymbol}{totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    )
}
